/**
 * Column resolution for the nested table, shared by the component and the
 * Design Builder's column editor so both always agree on what a level shows.
 *
 * A level's columns = explicit `columns` if given, else inferred from the keys
 * of that level's rows; then the level's own `columnOverrides`, then the
 * table-wide `levelOverrides[i].columns` (what the inspector writes) on top.
 */

import type {
  NestedTableColumn,
  NestedTableData,
  NestedTableLevel,
  NestedTableLevelOverride,
  NestedTableRow,
} from './nested-table.types';

/** Row keys that are structure / state, never a column. */
const RESERVED_KEYS = new Set(['expanded', 'id', 'children']);

/** `sales2025` → "Sales 2025", `oem_position` → "Oem Position". */
export function humanizeField(field: string): string {
  return field
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function childrenFieldOf(level: NestedTableLevel | undefined): string {
  return level?.childrenField || 'children';
}

/** Every row at each depth, top level first. */
export function rowsByLevel(data: NestedTableData, rows: NestedTableRow[]): NestedTableRow[][] {
  const out: NestedTableRow[][] = [];
  let current = rows;
  for (let index = 0; index < data.levels.length && current.length; index++) {
    out.push(current);
    const field = childrenFieldOf(data.levels[index]);
    current = current.flatMap((row) => (Array.isArray(row[field]) ? (row[field] as NestedTableRow[]) : []));
  }
  return out;
}

/** Columns from the union of row keys, in first-seen order, typed by value. */
export function inferColumns(rows: NestedTableRow[], childrenField: string): NestedTableColumn[] {
  const seen = new Map<string, unknown>();
  for (const row of rows.slice(0, 50)) {
    for (const [key, value] of Object.entries(row ?? {})) {
      if (key === childrenField || RESERVED_KEYS.has(key) || key.startsWith('_') || Array.isArray(value)) continue;
      if (!seen.has(key) || seen.get(key) === null || seen.get(key) === undefined) seen.set(key, value);
    }
  }
  return [...seen.entries()].map(([field, sample]) => {
    const numeric = typeof sample === 'number';
    return {
      field,
      label: humanizeField(field),
      type: numeric ? 'number' : 'text',
      ...(numeric ? { align: 'right' as const } : {}),
    } satisfies NestedTableColumn;
  });
}

/** The final, ordered column list for one level (hidden columns included). */
export function resolveLevelColumns(
  data: NestedTableData,
  levelIndex: number,
  levelRows: NestedTableRow[],
): NestedTableColumn[] {
  const level = data.levels[levelIndex];
  if (!level) return [];
  const base = level.columns?.length ? level.columns : inferColumns(levelRows, childrenFieldOf(level));
  const userOverrides = data.levelOverrides?.[levelIndex]?.columns ?? {};
  const merged = base.map((column) => ({
    ...column,
    ...defined(level.columnOverrides?.[column.field]),
    ...defined(userOverrides[column.field]),
    field: column.field,
  }));
  const order = data.levelOverrides?.[levelIndex]?.order ?? level.columnOrder;
  if (!order?.length) return merged;
  const rank = new Map(order.map((field, index) => [field, index]));
  return [...merged].sort((a, b) => (rank.get(a.field) ?? 1e6) - (rank.get(b.field) ?? 1e6));
}

/** A cleared override field means "use the default", not "set to undefined". */
function defined<T extends object>(patch: T | undefined): Partial<T> {
  if (!patch) return {};
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined)) as Partial<T>;
}

/** Level settings with the inspector's level-level edits (title, dense…) applied. */
export function resolveLevel(data: NestedTableData, levelIndex: number): NestedTableLevel | null {
  const level = data.levels[levelIndex];
  if (!level) return null;
  const override = data.levelOverrides?.[levelIndex];
  if (!override) return level;
  const { columns: _columns, order: _order, ...settings } = override;
  return { ...level, ...settings };
}

/** A level as the build form saves it in the payload. */
interface BuiltLevel {
  title?: string;
  columns?: Array<{ field?: string; label?: string }>;
}

/**
 * After the build form (Data tab) is regenerated, drop the Design-tab overrides
 * it just superseded — so the LAST edit wins, wherever it was made.
 *
 * The Design tab's `levelOverrides` sit on top of the build and would
 * otherwise beat it forever: rename a header in the Data tab after once
 * renaming it in the Design tab and the old name kept showing. Only what the
 * build actually changed is dropped (a column's header, a level's title, a
 * level's column order); every other override is kept.
 */
export function pruneSupersededOverrides(
  overrides: readonly NestedTableLevelOverride[],
  previous: readonly BuiltLevel[],
  next: readonly BuiltLevel[],
): NestedTableLevelOverride[] {
  return overrides.map((override, index) => {
    if (!override) return override;
    const before = previous[index] ?? {};
    const after = next[index] ?? {};
    const out: NestedTableLevelOverride = { ...override };

    if ((before.title ?? '') !== (after.title ?? '')) delete out.title;

    const beforeColumns = (before.columns ?? []).filter((column) => column.field);
    const afterColumns = (after.columns ?? []).filter((column) => column.field);
    const fieldsOf = (columns: typeof afterColumns) => columns.map((column) => column.field).join('|');
    if (afterColumns.length && fieldsOf(beforeColumns) !== fieldsOf(afterColumns)) delete out.order;

    if (out.columns) {
      const columns = { ...out.columns };
      afterColumns.forEach((column) => {
        const field = column.field!;
        const was = beforeColumns.find((item) => item.field === field)?.label;
        if (!columns[field] || was === column.label) return;
        const { label: _label, ...rest } = columns[field];
        if (Object.keys(rest).length) columns[field] = rest;
        else delete columns[field];
      });
      out.columns = columns;
    }
    return out;
  });
}
